import { memo } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Progress,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiAlertCircle, FiCheckCircle, FiFile, FiFolder, FiSkipForward, FiX } from 'react-icons/fi'
import { useAppDispatch } from '@/store'
import { UploadItem, uploadRemoved } from '@/store/slices/uploadsSlice'
import { resolveUploadConflict } from '@/store/thunks/uploadThunks'
import { useAppTranslation } from '@/contexts/LanguageContext'

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${Math.round((bytes / Math.pow(1024, exponent)) * 10) / 10} ${units[exponent]}`
}

interface UploadPanelRowProps {
  item: UploadItem
}

const UploadPanelRow = memo(function UploadPanelRow({ item }: UploadPanelRowProps) {
  const dispatch = useAppDispatch()
  const { t } = useAppTranslation('files')

  const subtleColor = useColorModeValue('gray.500', 'gray.400')
  const nameColor = useColorModeValue('gray.800', 'gray.100')
  const conflictBg = useColorModeValue('orange.50', 'whiteAlpha.100')
  const conflictBorder = useColorModeValue('orange.200', 'orange.700')
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50')

  const isSettled =
    item.status === 'completed' || item.status === 'failed' || item.status === 'skipped'

  const statusIcon = () => {
    switch (item.status) {
      case 'completed':
        return <FiCheckCircle size="15px" color="#38A169" />
      case 'failed':
        return <FiAlertCircle size="15px" color="#E53E3E" />
      case 'conflict':
        return <FiAlertCircle size="15px" color="#DD6B20" />
      case 'skipped':
        return <FiSkipForward size="15px" color="#718096" />
      default:
        return null
    }
  }

  const conflict = item.conflict
  const existing = conflict?.inTargetFolder ?? conflict?.elsewhere?.[0] ?? null

  const conflictMessage = () => {
    if (!existing) {
      return t('upload.duplicateGeneric')
    }

    if (conflict?.inTargetFolder) {
      return t('upload.duplicateHere', { name: existing.name })
    }

    return t('upload.duplicateElsewhere', {
      name: existing.name,
      path: existing.folder_path,
    })
  }

  return (
    <Box px={3} py={2.5} _hover={{ bg: hoverBg }}>
      <HStack spacing={2.5} align="flex-start">
        <Box color={subtleColor} pt="2px" flexShrink={0}>
          {item.fileCount ? <FiFolder size="16px" /> : <FiFile size="16px" />}
        </Box>

        <VStack spacing={1} align="stretch" flex={1} minW={0}>
          <HStack spacing={2} align="center">
            <Text fontSize="sm" fontWeight="medium" color={nameColor} noOfLines={1} flex={1}>
              {item.name}
            </Text>
            {statusIcon()}
            {isSettled && (
              <IconButton
                aria-label={t('upload.dismiss')}
                icon={<FiX size="13px" />}
                size="xs"
                variant="ghost"
                onClick={() => dispatch(uploadRemoved({ id: item.id }))}
              />
            )}
          </HStack>

          {(item.status === 'uploading' || item.status === 'hashing' || item.status === 'checking') && (
            <>
              <Progress
                value={item.status === 'uploading' ? item.progress : undefined}
                isIndeterminate={item.status !== 'uploading'}
                size="xs"
                colorScheme="blue"
                borderRadius="full"
              />
              <HStack justify="space-between">
                <Text fontSize="xs" color={subtleColor}>
                  {item.status === 'hashing' && t('upload.preparing')}
                  {item.status === 'checking' && t('upload.checkingDuplicates')}
                  {item.status === 'uploading' &&
                    (item.fileCount
                      ? t('upload.filesCount', { count: item.fileCount })
                      : formatBytes(item.size))}
                </Text>
                {item.status === 'uploading' && (
                  <Text fontSize="xs" color={subtleColor}>
                    {item.progress}%
                  </Text>
                )}
              </HStack>
            </>
          )}

          {item.status === 'completed' && (
            <Text fontSize="xs" color="green.500">
              {t('upload.uploaded')}
            </Text>
          )}

          {item.status === 'skipped' && (
            <Text fontSize="xs" color={subtleColor}>
              {t('upload.skipped')}
            </Text>
          )}

          {item.status === 'failed' && (
            <Text fontSize="xs" color="red.500" noOfLines={2}>
              {item.error}
            </Text>
          )}

          {item.status === 'conflict' && (
            <Box
              mt={1}
              p={2}
              bg={conflictBg}
              border="1px solid"
              borderColor={conflictBorder}
              borderRadius="md"
            >
              <Text fontSize="xs" color={nameColor} mb={2}>
                {conflictMessage()}
              </Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  colorScheme="orange"
                  onClick={() =>
                    dispatch(resolveUploadConflict({ id: item.id, action: 'upload' }))
                  }
                >
                  {t('upload.uploadAnyway')}
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => dispatch(resolveUploadConflict({ id: item.id, action: 'skip' }))}
                >
                  {t('upload.skip')}
                </Button>
              </HStack>
            </Box>
          )}
        </VStack>
      </HStack>
    </Box>
  )
})

export default UploadPanelRow
